export default class extends HTMLElement {

    constructor (config = {}) {
        super();

        // TODO: Remove delayConstruction from all configs and add as constructor property
        if (this.constructor.delayConstruction || config.delayConstruction) {
            return;
        }

        this.initialiseConstruction(config);
    }

    // Static

    static testableComponents = {};

    static get observedAttributes () {
        let observedAttributes = [];

        if (this.observedProps !== undefined) {
            observedAttributes = this.observedProps.map(prop => prop.names ?? prop.name).flat();
        }

        return observedAttributes;
    }

    // Public

    styleSelectors = new Set;

    set onConnectedCallback (value) {
        // TODO: Define onConnectedCallback as static property on all components
        this.#onConnectedCallback.push(value);
    }

    initialiseConstruction (config) {
        this.#initialiseComponent(config);
        this.#addGlobalProps();
        this.#initialiseProps();
        this.#initialiseComponentEvents();
        this.#initialiseExtensions();
    }

    // TODO: Update all references to use attachProps and reform prop structure on bases
    deprecatedAttachAdditionalProps (propArray) {
        propArray.forEach((propItem) => {
            this.#initialiseSingleProp(propItem.class, propItem.callback);
        });

        const newProps = propArray.map((prop) => this.props[prop.class.name]);

        this.#runPropCallbacks(newProps);
    }

    attachProps (propArray, propCallbackObject) {
        propArray.forEach((propItem) => {
            const propNameCamelCaseAlias = wealthify.convert.toCamelCase(propItem.name);

            this.#initialiseSingleProp(propItem, propCallbackObject[propNameCamelCaseAlias]);
        });

        const newProps = propArray.map((prop) => this.props[prop.name]);

        if (this.#hasHitConnectedCallback) {
            this.#runPropCallbacks(newProps);
        }
    }

    attributeChangedCallback (prop, oldValue, newValue) {
        // TODO: Potentially become confusing over time - refactor
        const currentValueUndefinedNull = this[prop] === undefined || this[prop] === null;
        const propPreloadedWithValuePriorToLoad = !currentValueUndefinedNull && this.isComponentReady === false;

        if (currentValueUndefinedNull || propPreloadedWithValuePriorToLoad) {
            return;
        }
        
        const isValueDifferentToAttributeOrInternal = oldValue !== newValue && newValue !== this[prop].toString();
        
        if (isValueDifferentToAttributeOrInternal) {
            this[prop] = newValue;
        }
    }

    connectedCallback () {
        if (this.isComponentReady) {
            return;
        }

        this.#hasHitConnectedCallback = true;

        // TODO: Create Slot class (or investigate native slots)
        this.#assignChildren();
        this.#runPropCallbacks();

        this.appendChild(this.template.firstElementChild);
        this.isComponentReady = true;

        if (this.#hasConnectedCallbacks) {
            this.#onConnectedCallback.forEach((callback) => callback());
        }

        if (!this.dom.root.classList.contains(this.styleSelector) && this.styleSelector) {
            // TODO: Apply styleSelector to all custom elements of components
            // (Some component currently add styleSelector to data-root element)
            this.styleSelectors.add(this.styleSelector);
        }

        this.classList.add(...this.styleSelectors);
        
        // TODO: Check whether used, maybe create class
        this.#initialiseMutationObserver();

        // TODO: Possibly remove this, check with test
        this.#assignTestId();
        this.#assignTestId2();
    }

    // Private

    #onConnectedCallback = [];
    #hasHitConnectedCallback = false;

    get #hasConnectedCallbacks () {
        return this.#onConnectedCallback.length > 0;
    }

    #addGlobalProps () {
        this.globalProps = [
            wealthify.props.HeightProp(),
            wealthify.props.Hide({name: 'hide'}),
            wealthify.props.Hide({name: 'hidemobile'}),
            wealthify.props.Hide({name: 'hidetablet'}),
            wealthify.props.Hide({name: 'hidedesktop'}),
            wealthify.props.MinWidth(),
            wealthify.props.MaxWidth(),
            wealthify.props.MinHeight(),
        ];

        // TODO: Move this to Container base
        if (this.hasChildren) {
            const hasChildrenProps = [
                wealthify.props.Sticky({name: 'sticky'}),
                wealthify.props.AlignFlex({name: 'alignitems'}),
                wealthify.props.AlignFlex({name: 'justifycontent'}),
                wealthify.props.AlignText(),
                wealthify.props.Direction({name: 'direction'}),
                
                new wealthify.Prop('constrain-desktop', {
                    type: wealthify.propTypes.Boolean(),
                    onChangeCallback: (value, component) => {
                        if(value){
                            component.style.maxWidth = wealthify.breakpoints.tabletwide.minWidth;
                        }else{
                            component.style.maxWidth = '';
                        }
                    }
                })
            ];
            
            this.globalProps = [
                ...this.globalProps,
                ...hasChildrenProps
            ];
        }
    }

    #initialiseComponent (config) {
        const template = config.templateHTML || this.constructor.template;

        // TODO: Remove templateHTML from all configs and add as constructor property
        if (template === undefined) {
            return wealthify.error.throwError(this.tagName, 'A "templateHTML" property has not been set in config.');
        }

        // TODO: Remove styleSelector from all configs and add as constructor property
        const styleSelector = config.styleSelector || this.constructor.styleSelector;

        if (styleSelector == undefined) {
            return wealthify.error.throwError(this.tagName, 'A "styleSelector" property has not been set in config or on instance.');
        }
        
        this.props = {};

        // TODO: Template should be processed using HTML Parser
        // Does using DOMParser gain on performance?
        this.template = this.#getTemplate(template);
        this.styleSelector = styleSelector;
        // TODO: Create component using Container base
        this.hasChildren = Object.prototype.hasOwnProperty.call(config, 'hasChildren') ? config.hasChildren : false;
        this.hasChildrenAssigned = this.hasChildren ? false : null;
        // TODO: Replace cacheDom config property with constructor property on all components
        this.dom = new wealthify.DomCache(this.template, this, config.cacheDom);
        //TODO: Create class for slot functionality (PDB1-1008).
        this.slots = {
            requiredSlots: this.#getSlotAttributesRelativeToComponent('[data-slot-required]', this.template),
            slotOrigins: this.#getSlotAttributesRelativeToComponent('[data-slot]', this.template),
            slotContents: this.#getSlotAttributesRelativeToComponent('[data-slot]', this),
        };
        
        this.isComponentReady = false;

        this.defaultSlotContents = config.defaultSlotContents;

        if (this.dom.root === null) {
            return wealthify.error.throwError(this.tagName, 'A data-root attribute has not been set');
        }
    }

    #getTemplate (templateHTML) {
        if (typeof templateHTML === 'string') {
            let template = document.createElement('div');
    
            template.innerHTML = templateHTML;
    
            return template.content ? template.content.cloneNode(true) : template;
        }

        return templateHTML;
    }

    #getSlotAttributesRelativeToComponent (attribute, element) {
        const slotElements = element.querySelectorAll(attribute);
        const validSlotElements = {};

        Array.prototype.forEach.call(slotElements, (slotElement) => {
            const parentIsCurrentComponent = slotElement.parentElement.tagName.toLowerCase() === this.tagName.toLowerCase();
            const isWebComponent = wealthify.dom.isWebComponent(slotElement.parentElement);
            
            if (isWebComponent && !parentIsCurrentComponent) {
                return;
            }

            const slotName = slotElement.getAttribute('data-slot');

            validSlotElements[slotName] = slotElement;
        });

        return validSlotElements;
    }

    #assignDomCacheSlots (slotOrigin, slotContent) {
        for (let elementName in this.slots.slotOrigins) {
            if (this.dom[elementName] === slotOrigin) {
                this.#assignDomCacheSlot(elementName, slotContent);
            }
        }
    }

    #assignDomCacheSlot (elementName, slotContent) {
        if (slotContent.length === 1) {
            slotContent = slotContent[0];
        }

        this.dom[elementName] = slotContent;
    }

    #initialiseProps () {
        if (!this.constructor.observedProps && !this.globalProps) {
            return;
        }

        const observedProps = this.constructor.observedProps ?? [];

        Object.values([...this.globalProps, ...observedProps]).forEach((propClass) => this.#initialiseSingleProp(propClass));
    }

    #initialiseSingleProp (propClass, propCallback) {
        // TODO: Potentially become confusing over time - refactor
        const isCamelCase = wealthify.convert.isCamelCase(propClass.name);
        
        if (isCamelCase) {
            return wealthify.error.throwError(this.tagName, `Prop ${propClass.name} cannot be camel-case, please use kebab-case (kebab-case props are given camel-case aliases for JavaScript).`);
        }

        const propNameCamelCaseAlias = wealthify.convert.toCamelCase(propClass.name);
        const customCallback = propCallback || this.propCallbacks?.[propClass.name] || this.propCallbacks?.[propNameCamelCaseAlias];
        const defaultCallback = propClass.onChangeCallback;

        if (!customCallback && !defaultCallback) {
            return wealthify.error.throwError(this.tagName, `A callback must be defined for ${propClass.name} prop`);
        }

        const complexValue = this[propClass.name] ?? this[propNameCamelCaseAlias];
        const basicValue = this.getAttribute(propClass.name); //Only the kebab-case variant is set on the custom element markup
        const defaultValue = propClass.default;

        propClass.componentName = this.tagName;
        propClass.value = complexValue || basicValue || defaultValue;
        propClass.onChangeCallback = customCallback || defaultCallback;

        this.#addPropGettersSetters(propClass, propClass.name);

        if (propNameCamelCaseAlias !== propClass.name) {
            this.#addPropGettersSetters(propClass, propNameCamelCaseAlias);
            this.constructor.observedAttributes.push(propNameCamelCaseAlias);
        }
    }

    #addPropGettersSetters (propClass, propName) {
        this.props[propName] = propClass;
        this.#defineGetterAndSetter(propName);
    }
    
    #defineGetterAndSetter (propName) {
        // TODO: Potentially become confusing over time - refactor
        Object.defineProperty(this, propName, {
            get () {  
                return this.props[propName].value;
            },
            set (value) {
                const isSameValue = value == this.props[propName].value;
                const isEmptyValueTypeCoercion = this.props[propName].value == '' && value !== '' && typeof this.props[propName].value !== typeof value;
                // e.g. to ensure changing '' to [] is possible
                
                if ((isSameValue || value === undefined) && !isEmptyValueTypeCoercion) {
                    return;
                }

                const isCamelCaseAlias = wealthify.convert.isCamelCase(propName);

                if (isCamelCaseAlias) {
                    const kebabCaseAlias = wealthify.convert.toKebabCase(propName);
                    
                    this[kebabCaseAlias] = value;

                    return;
                }

                const isComplexProp = this.props[propName].isComplex;

                if (value === '' && this.props[propName].default) {
                    value = this.props[propName].default;
                }

                this.props[propName].value = value;

                if (isComplexProp) {
                    this.removeAttribute(propName);
                } else {
                    this.setAttribute(propName, value);
                }

                if (this.#hasHitConnectedCallback) {
                    this.#runPropCallback(this.props[propName]);
                }
            }
        });
    }

    #assignTestId () {
        if (this.constructor.testableComponents[this.tagName]) {
            this.constructor.testableComponents[this.tagName]++;
        } else {
            this.constructor.testableComponents[this.tagName] = 1;
        }

        const testId = `${this.tagName.toLowerCase()}-${this.constructor.testableComponents[this.tagName]}`;

        this.setAttribute('data-test-id', testId);
    }

    #assignTestId2 () {
        if (this.label) {
            const labelCamelCase = this.label.toString()
                .replace(/[^a-zA-Z0-9 ]/g, '')
                .replace(/ ([a-zA-z0-9])/g, (all, group) => `-${group}`)
                .toLowerCase();
        
            this.setAttribute('data-test-id-2', labelCamelCase);
        }
    }

    #initialiseComponentEvents () {
        this.events = new wealthify.ComponentEvents(this, this.constructor?.eventSettings);
    }

    #assignChildren () {
        this.#assignSlottableChildren();

        if (this.hasChildren) {
            const childRoot = this.dom.childRoot ? this.dom.childRoot : this.dom.root;

            while (this.childNodes.length) {
                const isEmptyTextNode = this.firstChild.nodeType === 3 && this.firstChild.textContent.trim() === '';

                if (isEmptyTextNode) {
                    wealthify.dom.removeNodes([this.firstChild]);
                    continue;
                }

                this.hasChildrenAssigned = true;
                childRoot.appendChild(this.firstChild);
            }
        } else {
            wealthify.dom.removeAllParentsChildren(this);
        }
    }

    #assignMutation (mutation) {
        const slotContentObject = mutation.target.#getSlotObject(mutation.addedNodes);
        const nonSlotContent = Array.prototype.filter.call(mutation.addedNodes, (node) => {
            const isTextNode = node.nodeType === 3;
            const isNotSlot = node.getAttribute == undefined || node.getAttribute('data-slot') === null;
            
            return isTextNode || isNotSlot;
        });

        mutation.target.#assignSlottableChildren(slotContentObject);

        if (mutation.target.hasChildren) {
            const rootName = mutation.target.dom.childRoot ? 'childRoot' : 'root';
            const rootNode = mutation.target.dom[rootName];
            const allContentReplaced = rootNode.parentElement === null;

            if (allContentReplaced) {
                wealthify.dom.removeAllParentsChildren(rootNode);
            }

            Array.prototype.forEach.call(nonSlotContent, (node) => {
                if (node === rootNode) {
                    return;
                }

                mutation.target.dom[rootName].appendChild(node);
            });

            if (allContentReplaced) {
                mutation.target.appendChild(rootNode);
            }
        } else {
            wealthify.dom.removeNodes(nonSlotContent);
        }
    }

    #assignSlottableChildren (slotContentObject) {
        if (Object.keys(this.slots.slotOrigins).length === 0) {
            return;
        }

        this.#appendDefaultSlotContents();

        slotContentObject = slotContentObject || this.#getSlotObject(this.children);

        const slotOriginObject = this.slots.slotOrigins;
        const requiredSlotNames = [...Object.keys(this.slots.requiredSlots)];

        if (Object.keys(slotContentObject).length > 0) {
            const allRequiredSlotsPassed =  requiredSlotNames.length ? Object.values(requiredSlotNames).every((slotName) => wealthify.type.array.includes(slotName, Object.keys(slotContentObject))) : true;
            
            if (!allRequiredSlotsPassed) {
                return wealthify.error.throwError(this.tagName, `Slot contents passed to "${this.tagName}" did not match all required slots: ${requiredSlotNames.join(', ')}`);
            }
    
            Object.keys(slotContentObject).forEach((slotContentName) => {
                const slotContent = slotContentObject[slotContentName];
                const slotOrigin = slotOriginObject[slotContentName];

                if (slotOrigin === null) {
                    return wealthify.error.throwError(this.tagName, `Slot content "${slotContentName}" passed to "${this.tagName}" did not match a slot origin. If slot was added after component construction, the slot origin has already been filled.`);
                }

                const isMultiple = slotOrigin.hasAttribute('data-slot-multiple');
                const isMultipleContentSlots = Object.keys(slotContent).length > 1;

                if (!isMultiple && isMultipleContentSlots) {
                    return wealthify.error.throwError(this.tagName, `Multiple slot contents were passed to singular slot origin "${slotContentName}" in "${this.tagName}". If slot origin can accept multiple slot contents, add the data-slot-multiple attribute to the slot origin.`);
                }

                slotContent.forEach((slot) => {
                    Object.values(slotOrigin.attributes).forEach((attribute) => {
                        if (attribute.name === 'class') {
                            slotOrigin.classList.forEach((className) => slot.classList.add(className));
                        } else {
                            slot.setAttribute(attribute.name, attribute.value);
                        }
                    });

                    slot.removeAttribute('data-slot');
                    slot.setAttribute(`data-${wealthify.convert.toKebabCase(slotContentName)}`, '');
                    slot.slotData = slotOrigin.slotData;
                });
    
                this.#assignDomCacheSlots(slotOrigin, slotContent);
    
                slotOrigin.parentNode.replaceChild(slotContent[0], slotOrigin);

                Object.keys(this.slots.slotOrigins).forEach((slotName) => {
                    if (slotName === slotContentName) {
                        delete this.slots.slotOrigins[slotName];
                    }
                });
                

                if (isMultipleContentSlots) {
                    slotContent.forEach((slot, index) => {
                        if (index > 0) {
                            slotContent[0].parentNode.insertBefore(slot, slotContent[index - 1].nextSibling);
                        }
                    });
                }
            });
        } else {
            if (requiredSlotNames.length > 0) {
                return wealthify.error.throwError(this.tagName, `Slot contents passed to "${this.tagName}" did not match all required slots: ${requiredSlotNames.join(', ')}`);
            }
        }        
    }

    #getSlotObject (slotArray) {
        const slotObject = {};

        Array.prototype.forEach.call(slotArray, (slot) => {
            const slotValue = slot.getAttribute && slot.getAttribute('data-slot');
            const isTextNode = slot.nodeType === 3;
            const isSlot = slotValue !== null;

            if (!isTextNode && isSlot) {
                slotObject[slotValue] = slotObject[slotValue] || [];

                slotObject[slotValue].push(slot);
            }
        });

        return slotObject;
    }

    #appendDefaultSlotContents () {
        const slotContentValues = Object.keys(this.slots.slotContents);

        if (this.defaultSlotContents) {
            Object.keys(this.defaultSlotContents).forEach((slotName) => {
                const slotIsOveridden = wealthify.type.array.includes(slotName, slotContentValues);
    
                if (!slotIsOveridden) {
                    const elements = wealthify.dom.getNodeArrayFromString(this.defaultSlotContents[slotName]);

                    elements.forEach((element) => this.appendChild(element));                    
                }
            });
        }
    }

    #runPropCallback (prop) {
        return prop.runCallback(this);
    }

    #runPropCallbacks (props = this.props) {
        Object.values(props).forEach((prop) => {
            this.#runPropCallback(prop);
        });
    }

    #initialiseExtensions () {
        if (this.constructor.extensions) {
            this.constructor.extensions.forEach((extensionClass) => {
                const classInitialisation = new extensionClass(this);

                if (extensionClass.initialisationProperty) {
                    this[extensionClass.initialisationProperty] = classInitialisation;
                }
            });
        }
    }

    #initialiseMutationObserver () {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length) {
                    this.#assignMutation.bind(this)(mutation);
                }
            });
        });
      
        observer.observe(this, {
            childList: true,
            subTree: true 
        });
    }
}